import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Images } from "../../../../../../utils/imageConstants";
import type { ICommentaryBall, IMatchCommentary, InningScorecard } from "./types/matchDetails.d";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Api } from "../../../../../../services/interceptor";
import { QUERY_KEY, getLastNoversSummaryLatestParams, getLastNoversSummaryOlderQueryParams, type CommentaryApiType } from "../../../../../../services/queryEndPoint";
import { useParams } from "react-router-dom";

type CommentaryPageParam = "latest" | number;

type BallFilter = "all" | "boundaries" | "wickets";

/** True bowling order: earliest delivery first (e.g. 0.1 … 0.6). */
function sortCommentaryBallsChronologically(balls: ICommentaryBall[]): ICommentaryBall[] {
  return [...balls].sort((a, b) => {
    const tA = Number(a?.createdAt);
    const tB = Number(b?.createdAt);
    if (!Number.isNaN(tA) && !Number.isNaN(tB) && tA !== tB) {
      return tA - tB;
    }
    const bnA = Number(a?.ball_number);
    const bnB = Number(b?.ball_number);
    if (!Number.isNaN(bnA) && !Number.isNaN(bnB) && bnA !== bnB) {
      return bnA - bnB;
    }
    return (a?.ball_id ?? 0) - (b?.ball_id ?? 0);
  });
}

/** Latest delivery first (e.g. 0.6 … 0.1) after the over summary — same for every over of every innings. */
function sortCommentaryBallsDescending(balls: ICommentaryBall[]): ICommentaryBall[] {
  return [...sortCommentaryBallsChronologically(balls)].reverse();
}

type CommentaryOverIndexMode = "zero" | "one";

/**
 * Backends differ: some send the first over as 0 (balls 0.1–0.6), others as 1 (1.1–1.6).
 * Infer from loaded rows only — no dependency on fetch order beyond what’s in memory.
 */
function getCommentaryOverIndexMode(overs: IMatchCommentary[]): CommentaryOverIndexMode {
  let seen0 = false;
  let seen1 = false;
  for (const o of overs) {
    if (o?.overNumber === 0) seen0 = true;
    if (o?.overNumber === 1) seen1 = true;
    for (const b of o?.ballsResponseList ?? []) {
      if (b?.overNumber === 0) seen0 = true;
      if (b?.overNumber === 1) seen1 = true;
    }
  }
  if (seen0) return "zero";
  if (seen1) return "one";
  // Mid-innings chunk with no 0 or 1 yet (e.g. only overs 25–30): match 0-based scoring (+1 title).
  return "zero";
}

/** 1-based “END OF OVER” line: zero-mode → apiOver+1 (30.x → 31); one-mode → apiOver (1.x → 1). */
function endOfOverHeadingNumber(apiOverNumber: number | undefined, mode: CommentaryOverIndexMode): number {
  if (typeof apiOverNumber !== "number" || Number.isNaN(apiOverNumber)) return 1;
  if (mode === "one") return Math.max(1, apiOverNumber);
  return apiOverNumber + 1;
}

type MatchCommentaryProps = {
  scores?: InningScorecard[];
  /** From match details (same as polled summary commentary). */
  matchStatus?: string;
  activeCommentaryInningId?: string;
  /** Latest overs from parent `useQuery` — keeps Commentary tab in sync without relying on infinite refetch alone. */
  liveCommentarySnapshot?: IMatchCommentary[];
};

/** Keep commentary in sync with scorecard/summary while the selected innings is live. */
const LIVE_COMMENTARY_POLL_MS = 30_000;

const MatchesCommentary = ({ scores = [], matchStatus, activeCommentaryInningId, liveCommentarySnapshot }: MatchCommentaryProps) => {
  const { matchId } = useParams();
  const queryClient = useQueryClient();
  const [selectedTeamInningId, setSelectedTeamInningId] = useState<string>("");

  useEffect(() => {
    setSelectedTeamInningId("");
  }, [matchId]);
  const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isCommentaryTypeOpen, setIsCommentaryTypeOpen] = useState(false);
  /** Overs list: newest first (desc) vs earliest first (asc). */
  const [overSortOrder, setOverSortOrder] = useState<"desc" | "asc">("desc");
  const [commentaryApiType, setCommentaryApiType] = useState<CommentaryApiType>("FULLCOMMENTARY");
  /** UI-only: compact hides over-summary cards; still uses same API when full. */
  const [commentaryDisplayMode, setCommentaryDisplayMode] = useState<"full" | "compact">("full");

  const [ballFilter, setBallFilter] = useState<BallFilter>("all");
  const [selectedBatsmanId, setSelectedBatsmanId] = useState<string | null>(null);

  const teamDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const commentaryTypeRef = useRef<HTMLDivElement>(null);

  const anyMenuOpen = isTeamDropdownOpen || isSortDropdownOpen || isCommentaryTypeOpen;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(t)) {
        setIsTeamDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(t)) {
        setIsSortDropdownOpen(false);
      }
      if (commentaryTypeRef.current && !commentaryTypeRef.current.contains(t)) {
        setIsCommentaryTypeOpen(false);
      }
    };

    if (anyMenuOpen) {
      setTimeout(() => document.addEventListener("mousedown", handleClickOutside), 0);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anyMenuOpen]);

  const battingTeams = useMemo(() => {
    return scores?.filter((score) => Boolean(score?.inningId)) || [];
  }, [scores]);

  const inProgressInningId = useMemo(() => {
    const live = scores.find((inning) => inning.inningStatus === "IN_PROGRESS" || inning.inningStatus === "STARTED");
    return live?.inningId ?? "";
  }, [scores]);

  const resolvedActiveInningId = activeCommentaryInningId?.trim() ? activeCommentaryInningId : inProgressInningId;

  // Default innings: live innings first (second innings often wrongly defaulted to first row).
  useEffect(() => {
    if (!battingTeams?.length || selectedTeamInningId) return;
    if (matchStatus === "IN_PROGRESS" && resolvedActiveInningId) {
      const has = battingTeams.some((t) => t.inningId === resolvedActiveInningId);
      if (has) {
        setSelectedTeamInningId(resolvedActiveInningId);
        return;
      }
    }
    const live = battingTeams.find((s) => s.inningStatus === "IN_PROGRESS" || s.inningStatus === "STARTED");
    setSelectedTeamInningId((live ?? battingTeams[0])?.inningId ?? "");
  }, [battingTeams, selectedTeamInningId, matchStatus, resolvedActiveInningId]);

  // Get selected team name
  const selectedTeamName = useMemo(() => {
    const team = battingTeams?.find((t) => t?.inningId === selectedTeamInningId);
    if (!team) return "-";
    return team.battingTeamName?.trim() || team.bowlingTeamName?.trim() || "-";
  }, [battingTeams, selectedTeamInningId]);

  // Batsmen from the currently selected inning, split by status
  const currentInningBatsmen = useMemo(() => {
    const inning = scores?.find((s) => s?.inningId === selectedTeamInningId);
    return inning?.battingScoreCardResponseList ?? [];
  }, [scores, selectedTeamInningId]);

  const battingNowPlayers = useMemo(() => currentInningBatsmen.filter((b) => b?.status === "STRIKER" || b?.status === "NON_STRIKER"), [currentInningBatsmen]);

  const battedPlayers = useMemo(() => currentInningBatsmen.filter((b) => b?.status === "OUT"), [currentInningBatsmen]);

  const selectedBatsmanName = useMemo(() => {
    if (!selectedBatsmanId) return null;
    const p = currentInningBatsmen.find((b) => b?.playerId === selectedBatsmanId);
    if (!p) return null;
    return `${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim() || null;
  }, [currentInningBatsmen, selectedBatsmanId]);

  // Derive the API commentaryType from the active ball filter
  const effectiveCommentaryApiType = useMemo((): CommentaryApiType => {
    if (selectedBatsmanId) return "BATSMAN";
    if (ballFilter === "boundaries") return "BOUNDARIES";
    if (ballFilter === "wickets") return "WICKETS";
    return commentaryApiType;
  }, [selectedBatsmanId, ballFilter, commentaryApiType]);

  const shouldPollLiveCommentary = Boolean(matchId && selectedTeamInningId && resolvedActiveInningId && selectedTeamInningId === resolvedActiveInningId && matchStatus === "IN_PROGRESS");

  // Only merge the live snapshot when we're actually viewing the tail of the innings.
  // In asc mode the list starts at over 0 and grows forward — merging in the latest
  // overs would inject them at the end and create a visible gap for the unfetched
  // middle, which is confusing.
  const canMergeParentSnapshot = Boolean(
    liveCommentarySnapshot?.length && matchStatus === "IN_PROGRESS" && selectedTeamInningId && resolvedActiveInningId && selectedTeamInningId === resolvedActiveInningId && effectiveCommentaryApiType === "FULLCOMMENTARY" && !selectedBatsmanId && ballFilter === "all" && overSortOrder === "desc",
  );

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isCommentaryLoading,
    isError: isCommentaryError,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEY.getCommentary, "infinite", matchId, selectedTeamInningId, effectiveCommentaryApiType, selectedBatsmanId ?? "", overSortOrder],
    initialPageParam: "latest" as CommentaryPageParam,
    queryFn: async ({ pageParam }: { pageParam: CommentaryPageParam }) => {
      if (!selectedTeamInningId) return [];
      const params =
        pageParam === "latest"
          ? {
              inningId: selectedTeamInningId,
              ...getLastNoversSummaryLatestParams(effectiveCommentaryApiType, selectedBatsmanId),
            }
          : {
              inningId: selectedTeamInningId,
              ...getLastNoversSummaryOlderQueryParams(pageParam, effectiveCommentaryApiType, selectedBatsmanId),
            };
      const res = await Api.get(`${QUERY_KEY.getCommentary}`, { params });
      return (res?.data?.overSummaryList || []) as IMatchCommentary[];
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage?.length) return undefined;
      const overs = lastPage.map((o) => o.overNumber).filter((n): n is number => typeof n === "number" && !Number.isNaN(n));
      if (!overs.length) return undefined;
      const minOver = Math.min(...overs);
      // Stop once we already have over 0 (first over). When minOver === 1, still fetch anchor 0.
      if (minOver < 1) return undefined;
      const nextAnchor = minOver - 1;
      if (nextAnchor < 0) return undefined;
      if (typeof lastPageParam === "number" && lastPageParam === nextAnchor) {
        return undefined;
      }
      return nextAnchor;
    },
    enabled: Boolean(selectedTeamInningId && matchId),
    staleTime: shouldPollLiveCommentary ? LIVE_COMMENTARY_POLL_MS : 60 * 1000,
    refetchInterval: shouldPollLiveCommentary && !canMergeParentSnapshot ? LIVE_COMMENTARY_POLL_MS : false,
  });

  const sortedCommentaryData = useMemo(() => {
    const map = new Map<number, IMatchCommentary>();
    infiniteData?.pages.forEach((page) => {
      page?.forEach((over) => {
        if (over?.overNumber != null && typeof over.overNumber === "number") {
          map.set(over.overNumber, over);
        }
      });
    });
    if (canMergeParentSnapshot && liveCommentarySnapshot) {
      liveCommentarySnapshot.forEach((over) => {
        if (over?.overNumber != null && typeof over.overNumber === "number") {
          map.set(over.overNumber, over);
        }
      });
    }
    const list = Array.from(map.values());
    return list.sort((a, b) => (overSortOrder === "desc" ? b.overNumber - a.overNumber : a.overNumber - b.overNumber));
  }, [infiniteData, overSortOrder, canMergeParentSnapshot, liveCommentarySnapshot]);

  const filteredCommentaryData = useMemo(() => {
    const isFiltered = ballFilter !== "all" || selectedBatsmanId != null;
    if (!isFiltered) return sortedCommentaryData;

    return sortedCommentaryData
      .map((over) => {
        const filteredBalls = (over.ballsResponseList ?? []).filter((ball) => {
          if (ballFilter === "boundaries") {
            return ball?.boundary === true || ball?.runs_scored === 4 || ball?.runs_scored === 6;
          }
          if (ballFilter === "wickets") {
            return ball?.wicket?.wicket_id != null;
          }
          if (selectedBatsmanId) {
            return ball?.batsman?.playerId === selectedBatsmanId;
          }
          return true;
        });
        return { ...over, ballsResponseList: filteredBalls };
      })
      .filter((over) => over.ballsResponseList.length > 0);
  }, [sortedCommentaryData, ballFilter, selectedBatsmanId]);

  const overIndexMode = useMemo(() => getCommentaryOverIndexMode(sortedCommentaryData), [sortedCommentaryData]);

  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // In oldest-first mode, the API only paginates backward (latest → older), so we
  // drive fetchNextPage until every over is loaded. Only then do we reveal the list
  // (sorted asc from over 0). Without this, the user would see the list "start at
  // over 18" and shift backward batch-by-batch as pages arrive.
  const isAscPreloading = overSortOrder === "asc" && hasNextPage;

  useEffect(() => {
    if (isAscPreloading && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [isAscPreloading, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const node = loadMoreSentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handleLoadMore();
      },
      { root: null, rootMargin: "240px", threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [handleLoadMore, sortedCommentaryData.length]);

  const handleTeamSelect = (inningId: string) => {
    setSelectedTeamInningId(inningId);
    setIsTeamDropdownOpen(false);
    setBallFilter("all");
    setSelectedBatsmanId(null);
    setIsCommentaryTypeOpen(false);
  };

  // Wipe both sort caches for this innings so toggling always re-paginates from the
  // appropriate end (asc → over 0 forward; desc → latest backward) instead of showing
  // stale pages from a previous session under that key.
  const handleSortChange = (next: "asc" | "desc") => {
    setIsSortDropdownOpen(false);
    if (next === overSortOrder) return;
    if (matchId && selectedTeamInningId) {
      queryClient.removeQueries({
        queryKey: [QUERY_KEY.getCommentary, "infinite", matchId, selectedTeamInningId],
      });
    }
    setOverSortOrder(next);
  };

  return (
    <>
      <div className="commentary-wrapper">
        <div className={`tabs-parent${anyMenuOpen ? " tabs-parent--menu-open" : ""}`}>
          <div
            ref={sortDropdownRef}
            className="tab-item tab-item--dropdown"
            onClick={(e) => {
              e.stopPropagation();
              setIsSortDropdownOpen((o) => !o);
              setIsTeamDropdownOpen(false);
              setIsCommentaryTypeOpen(false);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsSortDropdownOpen((o) => !o);
              }
            }}
          >
            <img className="frame-icon" alt="" src={Images.comm_new_icon} />
            <div className="tab-label">{overSortOrder === "desc" ? "Latest first" : "Oldest first"}</div>
            <img className="tab-item-frame-icon" alt="" src={Images.calendar_dropdown_icon} />
            {isSortDropdownOpen && (
              <div className="commentary-filter-menu" onClick={(e) => e.stopPropagation()} role="listbox">
                <button type="button" className={`commentary-filter-menu__option${overSortOrder === "desc" ? " is-active" : ""}`} onClick={() => handleSortChange("desc")}>
                  Latest first
                </button>
                <button type="button" className={`commentary-filter-menu__option${overSortOrder === "asc" ? " is-active" : ""}`} onClick={() => handleSortChange("asc")}>
                  Oldest first
                </button>
              </div>
            )}
          </div>
          <div
            ref={teamDropdownRef}
            className="tab-item-tab-item tab-item--dropdown"
            onClick={(e) => {
              e.stopPropagation();
              setIsTeamDropdownOpen(!isTeamDropdownOpen);
              setIsSortDropdownOpen(false);
              setIsCommentaryTypeOpen(false);
            }}
          >
            <div className="tab-label">{selectedTeamName || "-"}</div>
            <img className="tab-item-frame-icon" alt="" src={Images.calendar_dropdown_icon} />
            {isTeamDropdownOpen && battingTeams?.length > 0 && (
              <div className="commentary-filter-menu" onClick={(e) => e.stopPropagation()}>
                {battingTeams.map((team) => (
                  <button
                    type="button"
                    key={team?.inningId || ""}
                    className={`commentary-filter-menu__option${selectedTeamInningId === team?.inningId ? " is-active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTeamSelect(team?.inningId || "");
                    }}
                  >
                    {team?.battingTeamName?.trim() || team?.bowlingTeamName?.trim() || "Innings"}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div
            ref={commentaryTypeRef}
            className="tab-item2 tab-item--dropdown"
            onClick={(e) => {
              e.stopPropagation();
              setIsCommentaryTypeOpen((o) => !o);
              setIsTeamDropdownOpen(false);
              setIsSortDropdownOpen(false);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsCommentaryTypeOpen((o) => !o);
              }
            }}
          >
            <div className="tab-label">{selectedBatsmanName ? selectedBatsmanName : ballFilter === "boundaries" ? "Boundaries" : ballFilter === "wickets" ? "Wickets" : commentaryDisplayMode === "compact" ? "Compact view" : "Full commentary"}</div>
            <img className="tab-item-frame-icon" alt="" src={Images.calendar_dropdown_icon} />
            {isCommentaryTypeOpen && (
              <div className="commentary-filter-menu commentary-filter-menu--full" onClick={(e) => e.stopPropagation()} role="listbox">
                <button
                  type="button"
                  className={`commentary-filter-menu__option${commentaryDisplayMode === "full" ? " is-active" : ""}`}
                  onClick={() => {
                    setCommentaryApiType("FULLCOMMENTARY");
                    setCommentaryDisplayMode("full");
                    setIsCommentaryTypeOpen(false);
                  }}
                >
                  Full commentary
                </button>
                <button
                  type="button"
                  className={`commentary-filter-menu__option${commentaryDisplayMode === "compact" ? " is-active" : ""}`}
                  onClick={() => {
                    setCommentaryApiType("FULLCOMMENTARY");
                    setCommentaryDisplayMode("compact");
                    setIsCommentaryTypeOpen(false);
                  }}
                >
                  Compact (ball-by-ball)
                </button>
                <div className="commentary-filter-menu__divider" />
                <button
                  type="button"
                  className={`commentary-filter-menu__option${ballFilter === "boundaries" ? " is-active" : ""}`}
                  onClick={() => {
                    setBallFilter(ballFilter === "boundaries" ? "all" : "boundaries");
                    setSelectedBatsmanId(null);
                    setIsCommentaryTypeOpen(false);
                  }}
                >
                  <span className="commentary-filter-menu__badge commentary-filter-menu__badge--4">4</span>
                  &nbsp;/&nbsp;
                  <span className="commentary-filter-menu__badge commentary-filter-menu__badge--6">6</span>
                  &nbsp; Boundaries
                </button>
                <button
                  type="button"
                  className={`commentary-filter-menu__option${ballFilter === "wickets" ? " is-active" : ""}`}
                  onClick={() => {
                    setBallFilter(ballFilter === "wickets" ? "all" : "wickets");
                    setSelectedBatsmanId(null);
                    setIsCommentaryTypeOpen(false);
                  }}
                >
                  <span className="commentary-filter-menu__badge commentary-filter-menu__badge--w">W</span>
                  &nbsp; Wickets
                </button>
                {currentInningBatsmen.length > 0 && (
                  <>
                    <div className="commentary-filter-menu__divider" />
                    {battingNowPlayers.map((p) => (
                      <button
                        type="button"
                        key={p?.playerId}
                        className={`commentary-filter-menu__option${selectedBatsmanId === p?.playerId ? " is-active" : ""}`}
                        onClick={() => {
                          setSelectedBatsmanId(p?.playerId ?? null);
                          setBallFilter("all");
                          setIsCommentaryTypeOpen(false);
                        }}
                      >
                        {p?.playerFirstName} {p?.playerLastName}
                        <span className="commentary-filter-menu__status commentary-filter-menu__status--batting">{p?.status === "STRIKER" ? "●" : "○"}</span>
                      </button>
                    ))}
                    {battedPlayers.map((p) => (
                      <button
                        type="button"
                        key={p?.playerId}
                        className={`commentary-filter-menu__option${selectedBatsmanId === p?.playerId ? " is-active" : ""}`}
                        onClick={() => {
                          setSelectedBatsmanId(p?.playerId ?? null);
                          setBallFilter("all");
                          setIsCommentaryTypeOpen(false);
                        }}
                      >
                        {p?.playerFirstName} {p?.playerLastName}
                        <span className="commentary-filter-menu__status commentary-filter-menu__status--out">out</span>
                      </button>
                    ))}
                    {selectedBatsmanId && (
                      <button
                        type="button"
                        className="commentary-filter-menu__option commentary-filter-menu__option--clear"
                        onClick={() => {
                          setSelectedBatsmanId(null);
                          setBallFilter("all");
                          setIsCommentaryTypeOpen(false);
                        }}
                      >
                        ✕ Clear batsman filter
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="commentary-content">
          <img alt="share-icon" src={Images.share_icon} />
        </div>
        {isCommentaryLoading && sortedCommentaryData.length === 0 && <div className="commentary-infinite-status">Loading commentary…</div>}
        {isCommentaryError && <div className="commentary-infinite-status commentary-infinite-status--error">Could not load commentary. Try again later.</div>}
        {isAscPreloading && !isCommentaryLoading && <div className="commentary-infinite-status">Loading commentary from the first over…</div>}
        {!isCommentaryLoading && !isAscPreloading && filteredCommentaryData.length === 0 && sortedCommentaryData.length > 0 && <div className="commentary-infinite-status">No results for this filter.</div>}
        <div className="commentary-infinite-scroll">
          {!isAscPreloading &&
            filteredCommentaryData?.map((overSummary, index) => {
              const apiOverNumber = typeof overSummary?.overNumber === "number" && !Number.isNaN(overSummary.overNumber) ? overSummary.overNumber : 0;
              const headingOverNumber = endOfOverHeadingNumber(overSummary?.overNumber, overIndexMode);
              const endOfOverTitle = `END OF OVER ${headingOverNumber}`;
              const ballsInOver = overSummary?.ballsResponseList && overSummary.ballsResponseList.length > 0 ? (overSortOrder === "asc" ? sortCommentaryBallsChronologically(overSummary.ballsResponseList) : sortCommentaryBallsDescending(overSummary.ballsResponseList)) : [];

              const overSummaryBlock =
                commentaryDisplayMode === "full" ? (
                  <section className="over-summary" role="region" aria-label={`${endOfOverTitle} summary`}>
                    <header className="over-summary-header">
                      <div className="header-left">
                        <h2 className="title">{endOfOverTitle}</h2>
                        <div className="meta">
                          <span className="meta-item">{overSummary?.overRuns || "-"} runs</span>
                          <span className="dot" aria-hidden="true" />
                          <span className="meta-item">
                            {overSummary?.overWickets} wicket{overSummary?.overWickets > 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>

                      <div className="header-right">
                        <p className="score">
                          Total : {overSummary?.totalRuns}/{overSummary?.totalWickets}
                        </p>
                        <div className="rate">
                          {(() => {
                            // CRR: use API value when > 0, else derive from totalRuns / overs completed
                            const crr = (overSummary?.currentRunRate ?? 0) > 0 ? overSummary!.currentRunRate : headingOverNumber > 0 && (overSummary?.totalRuns ?? 0) >= 0 ? overSummary!.totalRuns / headingOverNumber : null;

                            // RRR: use API value when > 0, else compute from target/remainingBalls
                            const isChasing = (overSummary?.target ?? 0) > 0;
                            const remainingBalls = overSummary?.remainingBalls ?? 0;
                            const runsNeeded = (overSummary?.target ?? 0) - (overSummary?.totalRuns ?? 0);
                            const rrr = (overSummary?.requiredRunRate ?? 0) > 0 ? overSummary!.requiredRunRate : isChasing && remainingBalls > 0 && runsNeeded > 0 ? (runsNeeded * 6) / remainingBalls : null;

                            return (
                              <>
                                <span className="meta-item">CRR: {crr != null ? crr.toFixed(2) : "-"}</span>
                                {rrr != null && <span className="meta-item">RRR: {rrr.toFixed(2)}</span>}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </header>
                    <section className="over-summary-team-wrap" aria-label="Team-wrap">
                      <section className="over-summary-batting" aria-label="Batting summary">
                        <div className="batting-inner">
                          <div className="names">
                            <p className="text">
                              {overSummary?.firstBatsman?.firstName || "-"} {overSummary?.firstBatsman?.lastName || ""}
                            </p>
                            <p className="text">
                              {overSummary?.secondBatsman?.firstName || "-"} {overSummary?.secondBatsman?.lastName || ""}
                            </p>
                          </div>
                          <div className="figures-right">
                            <p className="text-wrap">
                              {overSummary?.firstBatsman?.runs || "0"}({overSummary?.firstBatsman?.balls || "0"})
                            </p>
                            <p className="text">
                              {overSummary?.secondBatsman?.runs || "0"}({overSummary?.secondBatsman?.balls || "0"})
                            </p>
                          </div>
                        </div>
                      </section>
                      <section className="over-summary-bowling" aria-label="Bowling summary">
                        <div className="bowling-inner">
                          <div className="names">
                            <p className="text">
                              {overSummary?.bowler?.firstName || "-"} {overSummary?.bowler?.lastName || ""}
                            </p>
                          </div>
                          <div className="figures-right">
                            <p className="text-wrap">
                              ({overSummary?.bowler?.overs}-{overSummary?.bowler?.maidens}-{overSummary?.bowler?.runs}-{overSummary?.bowler?.wickets})
                            </p>
                          </div>
                        </div>
                      </section>
                    </section>
                  </section>
                ) : null;

              const ballsBlock = (
                <div className="commentary-section commentary-section--after-summary">
                  {ballsInOver?.map((ball, ballIndex) => (
                    <div key={`ov-${overSummary?.overNumber}-b-${ballIndex}-${ball?.ball_id ?? "x"}-${ball?.ball_number ?? ""}-${ball?.createdAt ?? ""}`} className="commentary-item">
                      <div className="ball-number">
                        <span className="over">
                          {apiOverNumber}.{ball?.ball_number ?? "-"}
                        </span>
                        {ball?.wicket?.wicket_id != null && (
                          <div className="wicket-red">
                            <div className="wicket">w</div>
                          </div>
                        )}
                        {ball?.wicket?.wicket_id == null && (
                          <>
                            {ball?.runs_scored === 0 && <span className="ball-indicator dot">•</span>}
                            {ball?.runs_scored === 1 && <span className="ball-indicator runs">1</span>}
                            {ball?.runs_scored === 2 && <span className="ball-indicator runs">2</span>}
                            {ball?.runs_scored === 3 && <span className="ball-indicator runs">3</span>}
                            {ball?.runs_scored === 4 && <span className="ball-indicator boundary">4</span>}
                            {ball?.runs_scored === 6 && <span className="ball-indicator boundary">6</span>}
                          </>
                        )}
                      </div>
                      <div className="commentary-content-data">
                        <div className="ball-description">
                          <strong>{ball?.commentary || "-"}</strong>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );

              // Oldest-first: balls play out (0.1 → 0.6) and the over-summary caps the over at the end.
              // Latest-first (default): over-summary headlines the block, then the latest ball down to the earliest.
              return (
                <div key={`commentary-over-block-${overSummary?.overNumber ?? index}`}>
                  <div className="team-and-innings">
                    {overSortOrder === "asc" ? (
                      <>
                        {ballsBlock}
                        {overSummaryBlock}
                      </>
                    ) : (
                      <>
                        {overSummaryBlock}
                        {ballsBlock}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          <div ref={loadMoreSentinelRef} className="commentary-load-sentinel" aria-hidden />
          {isFetchingNextPage && !isAscPreloading && <div className="commentary-infinite-status commentary-infinite-status--more">Loading earlier overs…</div>}
          {!hasNextPage && !isAscPreloading && sortedCommentaryData.length > 0 && <div className="commentary-infinite-status commentary-infinite-status--end">{overSortOrder === "asc" ? "End of commentary" : "Start of innings"}</div>}
        </div>
      </div>
    </>
  );
};

export default MatchesCommentary;
